"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { captureException } from "scripts/logging/error-report";
import { useQueryFlags } from "scripts/hooks/useQueryFlags";
import { IUser } from "services/UserContext";
import bootbox from "bootbox";
import {
  IPrechatField,
  ISupportChatContext,
  isSupportChatAvailable,
  isSupportChatEmbeddable,
  SUPPORT_CHAT,
  SUPPORT_CHAT_BOOTSTRAP_SRC,
  SUPPORT_CHAT_MAX_LENGTH,
  SUPPORT_CHAT_ORIGIN,
  toPrechatValue,
} from "utils/supportChat";
import { isLocal, isProduction } from "scripts/application/setting";
import { joinWithSpace } from "utils/helpers";

/**
 * "Get Support" -- a launcher for HAR's Salesforce Embedded Messaging channel,
 * the same one the Member Only Area footer exposes to REALTORs.
 *
 * AI Mode answers property questions; membership, billing and technical
 * questions belong with member services. This gives agents that hand-off
 * without leaving the product.
 *
 * Only agents see it, and only agents cause the Salesforce bundle to be
 * downloaded at all -- consumers and guests never touch this code path.
 *
 * A caller that already knows what the conversation is about can pass `subject`
 * and `department` to prefill the prechat form; both are optional, and with
 * neither of them the form behaves exactly as it did before.
 */

type Status =
  /** Bootstrap has not reported itself ready yet; render nothing. */
  | "idle"
  /** Ready to launch: the button is visible. */
  | "ready"
  /** launchChat() is in flight. */
  | "launching"
  /** The Salesforce panel owns the screen; our button steps aside. */
  | "open"
  /**
   * This host cannot embed the chat window (see isSupportChatEmbeddable). The
   * button is shown anyway so the UI stays consistent across environments and
   * stays reviewable; clicking it explains why instead of failing silently.
   */
  | "unsupported"
  /**
   * The bootstrap could not be loaded or initialized. Same treatment as
   * "unsupported": show the button and explain on click, rather than leave an
   * agent with no visible route to support and no idea why.
   */
  | "failed";

interface ISupportChatProps extends ISupportChatContext {
  user: IUser;
  className?: string;
}

/** Everything the prechat form is filled from, resolved at launch time. */
type IPrechatInput = ISupportChatContext & { user: IUser };

/**
 * Shown instead of the chat when the host fails the frame-ancestors rule.
 *
 * Off production that is the everyday case -- local and preview hosts -- and
 * the audience is developers, so the notice names the origin and the rule it
 * broke: the origin is the whole reason it was refused.
 *
 * On production it should be unreachable, since aimode.har.com and
 * aisearch.har.com both qualify. If it ever does fire there it means something
 * is misconfigured (an http:// variant of a live host, say) and the person
 * looking at it is an agent who wants help, not a diagnosis -- so they get a
 * plain apology and somewhere else to go, with no internals attached.
 */
function showUnsupportedNotice() {
  bootbox.alert({
    title: isProduction
      ? "Support Chat is unavailable"
      : "Support Chat is unavailable here",
    message: isProduction
      ? `<div class="text-left font_size--medium">
          Support Chat cannot be opened right now. Please try again later, or
          reach Member Services from the Members Only Area on HAR.com.
        </div>`
      : `<div class="text-left font_size--medium">
          Support Chat runs on HAR.com over a secure connection, so it cannot open
          on <strong>${window.location.origin}</strong>.
          <br /><br />
          The chat window is hosted by Salesforce, which only permits it to be
          embedded by <strong>https://*.har.com</strong> on the standard port. To
          reach member services, use the test or production site.
        </div>`,
    centerVertical: true,
    closeButton: true,
    backdrop: true,
  });
}

/**
 * Shown when the chat could not be loaded at all -- a blocked or failed script
 * fetch, or an init that threw. No origins or internals here: unlike the
 * frame-ancestors case this can happen in front of a real agent on production,
 * and a refresh genuinely is the first thing worth trying.
 */
function showLoadFailureNotice() {
  bootbox.alert({
    title: "Support Chat is unavailable",
    message: `<div class="text-left font_size--medium">
        Support Chat could not be loaded. Please refresh the page and try again.
        <br /><br />
        If it keeps happening, you can reach Member Services from the Members
        Only Area on HAR.com.
      </div>`,
    centerVertical: true,
    closeButton: true,
    backdrop: true,
  });
}

/**
 * The Salesforce bootstrap is a single-instance global that cannot be
 * initialized twice, so loading is tracked at module scope rather than in
 * component state: index.tsx renders under React.StrictMode, which
 * double-invokes effects in development, and the layout can remount.
 * Subscribers let however many component instances exist follow the one
 * underlying widget.
 */
let bootstrapStarted = false;
let isReady = false;
let hasFailed = false;
const readySubscribers = new Set<() => void>();
const failureSubscribers = new Set<() => void>();
/** The chat gave the screen back; show the launcher again. */
const dismissSubscribers = new Set<() => void>();
/** The chat took the screen; step aside. */
const expandSubscribers = new Set<() => void>();

/**
 * Methods the chat window posts to this page. Names taken from the bootstrap's
 * own message switch, not guessed.
 *
 * The two ways out of the chat post different methods, which is the whole
 * reason the launcher used to disappear for good: minimize posts
 * ESW_APP_MINIMIZE, but the close (X) button posts ESW_APP_RESET_INITIAL_STATE
 * -- the bootstrap tears the markup down and regenerates its own launcher,
 * which is parked offscreen for us, so listening only for the former left no
 * way back in short of a reload.
 */
const DISMISS_METHODS = ["ESW_APP_MINIMIZE", "ESW_APP_RESET_INITIAL_STATE"];
/**
 * Reopening from Salesforce's own minimized-state bubble bypasses our button,
 * so track it or the launcher would sit on top of an open chat.
 */
const EXPAND_METHODS = ["ESW_APP_MAXIMIZE"];

/**
 * Read at the moment the prechat form is populated rather than captured when
 * the listener is registered -- the user fetch resolves asynchronously, so the
 * fields are usually still empty at bootstrap time.
 */
let readPrechatFields: () => Record<string, IPrechatField> = () => ({});

function buildPrechatFields({
  user,
  subject,
  department,
}: IPrechatInput): Record<string, IPrechatField> {
  const fields: Record<string, IPrechatField> = {
    // Facts about the agent, so they are locked.
    _firstName: { value: user.firstname ?? "", isEditableByEndUser: false },
    _lastName: { value: user.lastname ?? "", isEditableByEndUser: false },
    _email: { value: user.email ?? "", isEditableByEndUser: false },
    Member_Number: {
      // Agent numbers arrive as a number from /api/v1/chatbot/user, but the
      // prechat API only accepts strings.
      value: user.member_number != null ? String(user.member_number) : "",
      isEditableByEndUser: false,
    },
    // Caller-supplied context is a suggestion rather than a fact, so it stays
    // editable -- see the note on Request_Type below.
    _subject: {
      value: toPrechatValue(subject, SUPPORT_CHAT_MAX_LENGTH.subject),
      isEditableByEndUser: true,
    },
  };

  // Only sent when the caller actually knows the department. Omitting it leaves
  // the form's own required-selection behaviour intact, which is the right
  // default: routing someone to the wrong queue is worse than asking.
  const requestType = toPrechatValue(
    department,
    SUPPORT_CHAT_MAX_LENGTH.department
  );
  if (requestType) {
    // Deliberately editable even though it is preselected. "Choose a
    // Department" is a required field, so a locked wrong value would strand the
    // agent with no way to correct it short of reloading the page.
    fields.Request_Type = { value: requestType, isEditableByEndUser: true };
  }

  return fields;
}

/**
 * Push the current user's details into the prechat form. Called both when the
 * widget first reports ready and again immediately before each launch, so a
 * user change since bootstrap (a re-auth that did not reload the page) cannot
 * send someone else's name and member number to member services.
 */
function applyPrechatFields() {
  try {
    window.embeddedservice_bootstrap?.prechatAPI.setVisiblePrechatFields(
      readPrechatFields()
    );
  } catch (e) {
    // Not fatal: the prechat form still renders, it just arrives blank.
    captureException(e);
  }
}

/**
 * Record that the widget will not come up, and let the mounted launchers know.
 * Reported to Sentry as well: silence here would mean nobody finds out the
 * support route is down for every agent.
 */
function markFailed(error: unknown) {
  captureException(error);
  if (hasFailed) return;
  hasFailed = true;
  failureSubscribers.forEach((notify) => notify());
}

function loadSupportChat(getFields: () => Record<string, IPrechatField>) {
  // Always refresh, even on a repeat call: a later mount has a fresher user.
  readPrechatFields = getFields;
  if (bootstrapStarted) return;
  bootstrapStarted = true;

  // Registered before init() so the ready event cannot be missed.
  window.addEventListener("onEmbeddedMessagingReady", () => {
    isReady = true;
    applyPrechatFields();
    readySubscribers.forEach((notify) => notify());
  });

  // The chat window reports its own show/hide transitions to the host page.
  window.addEventListener("message", (event: MessageEvent) => {
    if (event.origin !== SUPPORT_CHAT_ORIGIN) return;
    const method = event.data?.method;
    if (DISMISS_METHODS.includes(method)) {
      dismissSubscribers.forEach((notify) => notify());
    } else if (EXPAND_METHODS.includes(method)) {
      expandSubscribers.forEach((notify) => notify());
    }
  });

  const script = document.createElement("script");
  script.src = SUPPORT_CHAT_BOOTSTRAP_SRC;
  script.async = true;
  script.onload = () => {
    try {
      const esw = window.embeddedservice_bootstrap;
      if (!esw) {
        throw new Error(
          "Support chat bootstrap loaded without defining embeddedservice_bootstrap"
        );
      }
      esw.settings.language = "en_US";
      // Suppress the Salesforce launcher: this component provides the button.
      // Parking it offscreen as well as hiding it matches the Member Only Area
      // footer, where hiding alone still left a clickable region behind.
      esw.settings.hideChatButtonOnLoad = true;
      esw.settings.chatButtonPosition = "-100px,-100px";
      esw.settings.devMode = isLocal;
      esw.init(
        SUPPORT_CHAT.orgId,
        SUPPORT_CHAT.deploymentName,
        SUPPORT_CHAT.deploymentUrl,
        { scrt2URL: SUPPORT_CHAT.scrt2Url }
      );
    } catch (e) {
      markFailed(e);
    }
  };
  script.onerror = () =>
    markFailed(new Error("Failed to load support chat bootstrap"));

  // Deliberately never removed on unmount -- the bootstrap is not re-entrant,
  // and re-adding the tag would not re-run init() anyway.
  document.body.appendChild(script);
}

interface ISupportChatIconProps {
  /** Rendered width and height, in px. */
  size?: number;
}

// Decorative in both places it is used: the button carries its own aria-label,
// and the empty-state copy sits right next to the word "Support". Hidden from
// assistive tech so it is not announced as a nameless extra image.
export function SupportChatIcon({ size = 16 }: ISupportChatIconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="currentColor" className="bi bi-headset" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M8 1a5 5 0 0 0-5 5v1h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a6 6 0 1 1 12 0v6a2.5 2.5 0 0 1-2.5 2.5H9.366a1 1 0 0 1-.866.5h-1a1 1 0 1 1 0-2h1a1 1 0 0 1 .866.5H11.5A1.5 1.5 0 0 0 13 12h-1a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h1V6a5 5 0 0 0-5-5" />
    </svg>
  );
}

export function SupportChat({
  user,
  className,
  subject,
  department,
}: ISupportChatProps) {
  const { embed } = useQueryFlags();
  const enabled = isSupportChatAvailable(user, embed);
  // Scheme, host and port cannot change without a reload, so this is settled
  // for the life of the page.
  const [embeddable] = useState(isSupportChatEmbeddable);

  // Where the chat cannot be framed there is no bootstrap and so no ready
  // event; start on the button rather than waiting for one that never comes.
  const [status, setStatus] = useState<Status>(() =>
    embeddable ? "idle" : "unsupported"
  );

  // Kept current on every render so the bootstrap's one-time ready listener
  // always prefills from the latest values -- both a user who signed in without
  // reloading, and a subject or department the caller changed since mount.
  const prechatRef = useRef<IPrechatInput>({ user, subject, department });
  prechatRef.current = { user, subject, department };

  useEffect(() => {
    // Skipping the load on an unsupported host also keeps its console clean of
    // the CORS and frame-ancestors failures the bootstrap would otherwise hit.
    if (!enabled || !embeddable) return;

    // Only leave "idle"; a ready event that arrives while the panel is open
    // must not yank the button back on screen.
    const onReady = () => setStatus((s) => (s === "idle" ? "ready" : s));
    // Covers both minimize and close. After a close the next click starts a
    // fresh conversation, which is what the Member Only Area does too.
    const onDismiss = () => setStatus("ready");
    const onExpand = () => setStatus("open");
    // Never over an open chat: a late failure report should not put a button on
    // top of a conversation that is working.
    const onFail = () => setStatus((s) => (s === "open" ? s : "failed"));

    readySubscribers.add(onReady);
    dismissSubscribers.add(onDismiss);
    expandSubscribers.add(onExpand);
    failureSubscribers.add(onFail);
    loadSupportChat(() => buildPrechatFields(prechatRef.current));
    // The widget may already have settled before this instance mounted.
    if (isReady) onReady();
    else if (hasFailed) onFail();

    return () => {
      readySubscribers.delete(onReady);
      dismissSubscribers.delete(onDismiss);
      expandSubscribers.delete(onExpand);
      failureSubscribers.delete(onFail);
    };
  }, [enabled, embeddable]);

  const launch = useCallback(() => {
    if (!embeddable) {
      showUnsupportedNotice();
      return;
    }
    const esw = window.embeddedservice_bootstrap;
    // Read the global rather than trusting `status`: a launcher showing after a
    // failure can still work if the script turned up late. Only when it really
    // is not there does the click explain itself -- silently doing nothing is
    // the one outcome worth ruling out.
    if (!esw?.utilAPI) {
      showLoadFailureNotice();
      return;
    }
    setStatus("launching");
    // Refresh the prefill against the user as they are right now, not as they
    // were when the widget booted.
    applyPrechatFields();
    // launchChat() is wrapped as well as chained: a synchronous throw would
    // otherwise never reach .catch and would leave the button stuck disabled
    // on "Connecting...".
    try {
      esw.utilAPI
        .launchChat()
        .then(() => setStatus("open"))
        .catch((e: unknown) => {
          captureException(e);
          // Put the button back rather than stranding the user on a spinner.
          setStatus("ready");
        });
    } catch (e) {
      captureException(e);
      setStatus("ready");
    }
  }, [embeddable]);

  if (!enabled || status === "idle" || status === "open") return null;

  const isLaunching = status === "launching";

  return (
    <button
      type="button"
      className={joinWithSpace("ai_support_chat btn_icon btn_header d-flex align-items-center pl-3 pr-3 pr-md-0", className)}
      onClick={launch}
      disabled={isLaunching}
      // The label is hidden by CSS below the md breakpoint, so the button needs
      // an accessible name of its own -- and it has to track the state, since a
      // static "Get Support" would hide the fact that a launch is under way.
      aria-label={isLaunching ? "Connecting to member services" : "Get Support"}
      aria-busy={isLaunching}
      title={
        isLaunching
          ? "Connecting to member services"
          : "Chat with HAR member services"
      }
    >
      {isLaunching ? (
        // Purely decorative: aria-busy above is what announces the state, so a
        // role="status" here would only duplicate it.
        <span
          className="spinner-border spinner-border-sm ai_support_chat_icon"
          aria-hidden="true"
        />
      ) : (
        <SupportChatIcon size={18} />
      )}
      <span className="ai_support_chat_label pl-1">
        {isLaunching ? "Connecting..." : "Support"}
      </span>
    </button>
  );
}

export default SupportChat;
